import React from "react";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import { useAuth0 } from "@auth0/auth0-react";
import { OrgAddress, readOrgAddresses } from "services/addresses.service";

type Props = { orgId: number };

type AddressRow = {
  id: string;
  address1: string;
  address2: string;
  town: string;
  city: string;
  zip: string;
};

const PhoneNumbersList = ({ orgId }: Props) => {
  const { getAccessTokenSilently } = useAuth0();
  const [rows, setRows] = React.useState<AddressRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (orgId <= 0) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data, error: apiError } = await readOrgAddresses(token, orgId);
        if (cancelled) return;
        if (apiError) {
          setError(apiError.message);
          return;
        }
        setRows(
          (data ?? []).map((a: OrgAddress) => ({
            id: String(a.addressId),
            address1: a.address1,
            address2: a.address2 ?? "",
            town: a.town ?? "",
            city: a.city ?? "",
            zip: a.zip ?? "",
          }))
        );
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, getAccessTokenSilently]);

  return (
    <div>
      <Table<AddressRow>
        loading={loading}
        columns={{
          address1: "Address 1",
          address2: "Address 2",
          town: "Town",
          city: "City",
          zip: "Zipcode",
        }}
        rows={rows}
      />
      {error && <div className="mt-3 text-sm text-red-500">{error}</div>}

      <div className="mt-4">
        <PaginationBar from={1} to={rows.length} total={rows.length} />
      </div>
    </div>
  );
};

export default PhoneNumbersList;
